"use client";
import React from 'react';
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { useRouter } from 'next/navigation';
import { FiLogIn } from 'react-icons/fi';
import { useTenantContext } from '@/contexts/TenantContext';
import { TenantLogo } from '@/components/tenant/assets/TenantLogo';

interface TenantHeaderProps {
  subdomain: string;
  /**
   * Opcional de propósito: as páginas de listagem e a home passam a contagem,
   * e `TenantNotFound` renderiza o header sem ela — não há o que contar quando
   * o tenant não foi encontrado.
   */
  imoveisCount?: number;
}

export default function TenantHeader({ subdomain, imoveisCount }: TenantHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const router = useRouter();

  // ✅ Usar contexto do tenant para obter dados dinâmicos
  const { tenant, isLoading } = useTenantContext();
  const config = tenant?.configurations || {};

  const primary       = config.cor_primaria       || '#1B365D';
  const secondary     = config.cor_secundaria     || '#A67C3B';
  const nameSecondary = config.cor_nome_secundaria || secondary;

  const btnStyle: React.CSSProperties = btnHover
    ? { background: `linear-gradient(to right, ${primary}, ${secondary})`, color: '#fff', transform: 'scale(1.05)' }
    : { backgroundColor: nameSecondary, color: '#000', transform: 'scale(1)' };

  const links = [
    { href: "/", label: "Página inicial" },
    { href: "/imoveis", label: imoveisCount !== undefined ? `Imóveis (${imoveisCount})` : "Imóveis" },
    { href: "/contato", label: "Contato" },
  ];

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginOpen(false);
    toast({ title: "Login realizado (mock)!" });
    setEmail("");
    setPassword("");
  };

  // ✅ Renderizar nome personalizado com cores do tenant
  const renderizarNomePersonalizado = () => {
    if (!tenant?.nome) {
      return (
        <span className="text-2xl font-serif font-bold tracking-tight uppercase select-none whitespace-nowrap">
          <span className="text-[#1B365D]">WESLEY</span> <span className="text-[#A67C3B]">REIS</span>
        </span>
      );
    }

    // ✅ Lógica inteligente: dividir o nome da tenant em partes
    const partes = tenant.nome.split(' ');
    
    if (partes.length < 2) {
      // ✅ Se só tem uma palavra, usar cor primária
      return (
        <span className="text-2xl font-serif font-bold tracking-tight uppercase select-none whitespace-nowrap">
          <span style={{ color: config.cor_nome_primaria || config.cor_primaria || '#1B365D' }}>
            {tenant.nome}
          </span>
        </span>
      );
    }

    // ✅ Se tem duas ou mais palavras: primeira em cor primária, resto em cor secundária
    const primeiraParte = partes[0];
    const resto = partes.slice(1).join(' ');

    return (
      <span className="text-2xl font-serif font-bold tracking-tight uppercase select-none whitespace-nowrap">
        <span style={{ color: config.cor_nome_primaria || config.cor_primaria || '#1B365D' }}>
          {primeiraParte}
        </span>{' '}
        <span style={{ color: config.cor_nome_secundaria || config.cor_secundaria || '#A67C3B' }}>
          {resto}
        </span>
      </span>
    );
  };

  return (
    <header className="bg-[#F8F5F0] border-b border-[#E5E0D8] w-full sticky top-0 z-50">
      <div className="container flex items-center justify-between py-3 px-4 min-h-[64px]">
        <div className="flex items-center gap-3">
          {/* ✅ Logo dinâmico usando novo sistema de assets */}
          <TenantLogo
            size="sm"
            className="w-10 h-10 rounded bg-[#2C2C2C] shadow-sm"
            alt={`Logo ${tenant?.nome || 'Imobiliária'}`}
            showFallback={true}
            priority={false}
          />
          {/* ✅ Renderizar nome dinâmico da tenant */}
          {renderizarNomePersonalizado()}
        </div>
        <nav className="hidden md:flex gap-8 ml-auto">
          {links.map(link => (
            <a
              key={link.href + link.label}
              href={link.href}
              className="font-medium transition-colors text-base capitalize"
              style={{ color: '#2C2C2C' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = nameSecondary)}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#2C2C2C')}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <Button
          className="hidden md:flex items-center gap-2 font-bold px-6 py-2 rounded-lg border-0 shadow-none transition-all duration-200 ml-8"
          style={btnStyle}
          onClick={() => router.push('/login')}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
        >
          <FiLogIn className="w-5 h-5 transition-colors" style={{ color: btnHover ? '#fff' : '#000' }} />
          <span>Área do Corretor</span>
        </Button>
        <button
          className="md:hidden flex flex-col space-y-1 ml-4"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Abrir menu"
        >
          <span className="w-7 h-1 bg-[#1B365D] rounded" />
          <span className="w-7 h-1 bg-[#1B365D] rounded" />
          <span className="w-7 h-1 bg-[#1B365D] rounded" />
        </button>
      </div>
      {menuOpen && (
        <nav className="md:hidden flex flex-col gap-2 bg-[#F8F5F0] border-t border-[#E5E0D8] px-4 pb-4 animate-in fade-in">
          {links.map(link => (
            <a
              key={link.href + link.label}
              href={link.href}
              className="font-medium transition-colors py-2 text-base capitalize"
              style={{ color: '#2C2C2C' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = nameSecondary)}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#2C2C2C')}
            >
              {link.label}
            </a>
          ))}
          <Button
            className="flex items-center gap-2 font-bold w-full mt-2 border-0 shadow-none transition-all duration-200"
            style={btnStyle}
            onClick={() => router.push('/login')}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
          >
            <FiLogIn className="w-5 h-5 transition-colors" style={{ color: btnHover ? '#fff' : '#000' }} />
            <span>Área do Corretor</span>
          </Button>
        </nav>
      )}
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Login</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input placeholder="E-mail" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            <Input placeholder="Senha" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            <Button type="submit" className="w-full">Entrar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </header>
  );
}
